/**
 * Classements joueurs du tournoi : trois colonnes (Buteurs / Passeurs / Notes),
 * agrégées côté serveur depuis l'archive Supabase (coût API nul). Rendu PUR
 * (données → DOM), sans accès réseau. Les lignes portent `data-player`/`data-name`
 * → la fiche joueur au survol (playerStatsTip) se branche dessus gratuitement.
 */
import type { LeaderRow, Leaderboards } from "../../app/leaderboards.ts";
import { tournamentXiHtml } from "./tournamentXI.ts";
import { esc, flagCode } from "../html.ts";
import { t } from "../../app/i18n.ts";

type Kind = "goals" | "assists" | "rating";

const rateCls = (r: number): string => (r >= 7.5 ? "hi" : r >= 6.5 ? "mid" : "lo");

function avatar(row: LeaderRow): string {
  if (row.photo) {
    return `<img class="lb-av" src="${esc(row.photo)}" alt="" loading="lazy" referrerpolicy="no-referrer">`;
  }
  return `<span class="lb-av lb-av-ph"><i class="ti ti-user" aria-hidden="true"></i></span>`;
}

function rowHtml(r: LeaderRow, i: number, kind: Kind): string {
  const name = r.name ?? "—";
  const valTxt =
    kind === "rating" ? (r.rating != null ? r.rating.toFixed(2) : "—") : String(kind === "assists" ? r.assists : r.goals);
  const valCls = kind === "rating" && r.rating != null ? ` lb-rate ${rateCls(r.rating)}` : "";
  const unit = kind === "rating" ? "" : ` ${kind === "assists" ? t("lb.unit.assist.one") : t("lb.unit.goal.one")}${(kind === "assists" ? r.assists : r.goals) > 1 ? "s" : ""}`;
  const rankCls = i < 3 ? ` r${i + 1}` : "";
  const team = r.teamCode
    ? `<span class="f fi fi-${esc(flagCode(r.teamCode))}"></span><span class="lb-cd">${esc(r.teamCode)}</span>`
    : "";
  const tag = Number.isInteger(r.id) ? ` data-player="${r.id}" data-name="${esc(name)}" tabindex="0"` : "";
  return `<li class="lb-row"${tag}>
    <span class="lb-rank${rankCls}">${i + 1}</span>
    ${avatar(r)}
    <span class="lb-pid">
      <span class="lb-name">${esc(name)}</span>
      <span class="lb-team">${team}<span class="lb-apps">${t("lb.apps", { n: r.apps })}</span></span>
    </span>
    <span class="lb-val${valCls}" aria-label="${esc(valTxt)}${unit}">${esc(valTxt)}</span>
  </li>`;
}

function listHtml(list: readonly LeaderRow[], kind: Kind): string {
  if (!list.length) return `<li class="lb-empty">${t("lb.empty")}</li>`;
  return list.map((r, i) => rowHtml(r, i, kind)).join("");
}

// Clés i18n (pas le texte) — résolues au RENDU : COLS est évalué à l'import, AVANT
// initLang(), donc un t() ici figerait la langue au français.
const COLS: ReadonlyArray<{ key: keyof Leaderboards; kind: Kind; icon: string; titleKey: string }> = [
  { key: "topScorers", kind: "goals", icon: "ti-ball-football", titleKey: "lb.col.scorers" },
  { key: "topAssists", kind: "assists", icon: "ti-shoe", titleKey: "lb.col.assists" },
  { key: "topRated", kind: "rating", icon: "ti-star", titleKey: "lb.col.ratings" },
];

/** Rend la section « Classements joueurs ». `data` doit être non-null. */
export function renderLeaderboards(el: HTMLElement, data: Leaderboards): void {
  el.innerHTML = `
    <h2 class="sec-title">${t("lb.section.title")} <span class="count">${t("lb.section.count")}</span></h2>
    ${data.xi ? tournamentXiHtml(data.xi) : ""}
    <div class="lb-grid">
      ${COLS.map(
        (c) => `<div class="card lb-col">
        <div class="lb-head"><i class="ti ${c.icon}" aria-hidden="true"></i><span>${t(c.titleKey)}</span></div>
        <ol class="lb-list">${listHtml(data[c.key] as readonly LeaderRow[], c.kind)}</ol>
      </div>`,
      ).join("")}
    </div>`;
}

/** Vrai si au moins un classement a une entrée (sinon on masque la section). */
export function hasLeaderData(data: Leaderboards | null): data is Leaderboards {
  return !!data && (data.topScorers.length > 0 || data.topAssists.length > 0 || data.topRated.length > 0);
}
